import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { GameProgress, LevelProgress } from '@/common/types/student';
import { CheckIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { memo, useMemo } from 'react';
import styled from 'styled-components';

const prettyGameNames: { [game: string]: string } = {
  mysticMatch: 'Mystic Match',
  wordWeaver: 'Word Weaver',
  phonemeForge: 'Phoneme Forge',
  trickyWords: 'Tricky Words',
  sentenceBuilder: 'Sentence Builder',
  wordRifts: 'Word Rifts',
  wordSums: 'Word Sums',
  morphMaster: 'Morph Master',
};

type LevelProgressionIndicatorProps = {
  currentLevelProgress: LevelProgress;
};

export default memo(function LevelProgressIndicator({
  currentLevelProgress,
}: LevelProgressionIndicatorProps) {
  const { levelId, completedPlaythroughs, ...currentLevelGameProgress } =
    currentLevelProgress;
  const levelData = scopeAndSequence.levels.find(
    (level) => level.id === levelId,
  );

  // Ensure games are rendered in scope-and-sequence order
  const gameProgress = useMemo(() => {
    if (!levelData) return currentLevelGameProgress;
    const orderedGamesProgress: { [gameName: string]: GameProgress } = {};
    for (const game of levelData.games) {
      orderedGamesProgress[game] = currentLevelGameProgress.gamesProgress[game];
    }
    return { ...currentLevelGameProgress, gamesProgress: orderedGamesProgress };
  }, [currentLevelProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex items-center gap-1.5">
      {Object.entries(gameProgress.gamesProgress).map(([game, progress]) => (
        <Tooltip key={game}>
          <TooltipTrigger asChild>
            <div>
              {progress.currentHighestMastery > 90 ? (
                <GameCompleteIndicator>
                  <CheckIcon size={10} weight="bold" />
                </GameCompleteIndicator>
              ) : (
                <GameProgressCircle mastery={progress.currentHighestMastery} />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {prettyGameNames[game]}:{' '}
            {Math.round(progress.currentHighestMastery)}% mastery
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
});

const GameCompleteIndicator = styled.div`
  height: 16px;
  width: 16px;
  background-color: hsl(244.07, 26.46%, 56.27%); // Spellcaster Purple™
  border-radius: 100%;
  display: grid;
  place-items: center;
  color: #ffffff;
`;

interface GameProgressCircleProps {
  mastery: number;
}

const GameProgressCircle = ({ mastery }: GameProgressCircleProps) => {
  // Convert mastery percentage to radians
  const percentage = Math.min(Math.max(mastery, 0), 100) / 100;
  const degrees = percentage * 360;
  const radians = (degrees * Math.PI) / 180;

  // Calculate the path for the pie slice
  const outerRadius = 11;
  const innerRadius = 8;
  const center = { x: 12, y: 12 };

  // Calculate end point of arc using the inner radius
  const endX = center.x + innerRadius * Math.cos(radians);
  const endY = center.y + innerRadius * Math.sin(radians);

  // Generate the SVG path for the pie slice
  const path =
    percentage === 0
      ? ''
      : percentage === 1
        ? `M ${center.x},${center.y} 
         m -${innerRadius},0 
         a ${innerRadius},${innerRadius} 0 1,1 ${innerRadius * 2},0 
         a ${innerRadius},${innerRadius} 0 1,1 -${innerRadius * 2},0`
        : `M ${center.x},${center.y}
         L ${center.x + innerRadius},${center.y}
         A ${innerRadius},${innerRadius} 0 ${
           percentage > 0.5 ? 1 : 0
         },1 ${endX},${endY}
         L ${center.x},${center.y}`;

  return (
    <CircleContainer>
      <ProgressSVG viewBox="0 0 24 24">
        <OutlineCircle cx="12" cy="12" r={outerRadius} />
        <ProgressSlice d={path} $mastery={mastery} />
      </ProgressSVG>
    </CircleContainer>
  );
};

const CircleContainer = styled.div`
  height: 16px;
  width: 16px;
`;

const ProgressSVG = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const OutlineCircle = styled.circle`
  fill: none;
  stroke: #fbbf24;
  stroke-width: 2;
`;

const ProgressSlice = styled.path<{ $mastery: number }>`
  fill: #fbbf24;
`;
